"use client"

import { useCallback, useState } from "react"

import { useRouter } from "@i18n/navigation"
import { startTrialBinding } from "@lib/data/trial"
import { isPaypalApprovalUrl } from "@lib/util/next-path"

import { useTrialClaim } from "./context"

/**
 * How a bind attempt ended, for callers that want to say something about it.
 *
 * `navigating` — the browser is leaving for the provider's approval page.
 * `bound` — no approval was needed: the customer already owned a chargeable
 *           method and the engine adopted it, so the trial is bound already.
 * `failed` — `error` carries the copy key.
 */
export type TrialBindStartOutcome = "navigating" | "bound" | "failed"

/**
 * Starts the PayPal vault binding and navigates to the approve URL
 * (2026-10-02 walkthrough, T06).
 *
 * Shared by the dashboard banner, the floating banner and the modal so every
 * "bind" entry point runs the same validated flow: the server-provided URL
 * must pass the https/PayPal shape check before it is ever assigned (never a
 * raw open redirect).
 *
 * `error` is a copy key, not a sentence — the caller owns the wording.
 */
export function useStartTrialBinding() {
  const router = useRouter()
  // The claim state is a client-side fetch keyed on a nonce; router.refresh()
  // re-renders the server tree but does NOT re-run it. Without this the
  // callout keeps offering "Claim" after a bind that never left the page.
  const { refresh: refreshTrialState } = useTrialClaim()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = useCallback(
    async (
      subscriptionId: string | null | undefined
    ): Promise<TrialBindStartOutcome> => {
      if (!subscriptionId) {
        setError("trial_bind_failed")
        return "failed"
      }

      setBusy(true)
      setError(null)

      const result = await startTrialBinding(subscriptionId).catch(() => ({
        status: "error" as const,
        message: "trial_bind_failed",
      }))

      if (result.status === "ok") {
        if (result.phase === "bound") {
          // No provider round trip: the engine adopted a method the customer
          // already had on file, and the trial is bound and extended. The
          // server tree still describes the unbound trial, so re-render it —
          // the bind callout only exists while no method is on file.
          setBusy(false)
          router.refresh()
          refreshTrialState()
          return "bound"
        }

        if (!isPaypalApprovalUrl(result.approveUrl)) {
          setBusy(false)
          setError("trial_bind_failed")
          return "failed"
        }

        window.location.assign(result.approveUrl)
        return "navigating"
      }

      setBusy(false)
      setError(result.message)
      return "failed"
    },
    [router, refreshTrialState]
  )

  return { start, busy, error }
}
